import React from 'react';
import classNames from 'classnames';
import './RequestCard.scss';
import { prefixClass } from 'lib/utils';
import { AvatarMini, DueDateBadge, Tags, Tooltip, HighlightText } from '../../../../Common';
import {
  IconMention,
  IconAttachment,
  IconWSBacklog,
  IconWSTodo,
  IconWSInProgress,
  IconWSClosed,
  IconPromote,
} from 'components/Icons';
import type { AssignedToType } from '../../../../types/Todos';
import type { User } from '@hone-automation/common';
import { formatMessageContentToPlainText } from '../../../../Cards/MessagesCard/subcomponents/MessageContent/formatMessageContent';

/** Work state represents the lifecycle stage of a request */
export type WorkState = 'backlog' | 'todo' | 'in-progress' | 'blocked' | 'closed';

export type RequestCardAssignee = {
  /** Display name for initials */
  name?: string;
  /** Avatar type/style */
  type: AssignedToType;
  /** Whether this is an explicit assignment (vs fallback to last commenter) */
  isExplicit?: boolean;
};

export type RequestCardProps = {
  /** Unique identifier for the request */
  id: string;
  /** Title/identifier of the request */
  title: string;
  /** Date label to display (compact; depends on grouping) */
  date: string;
  /** Full date/time tooltip content */
  dateTooltip?: string;
  /** Assignee info for avatar */
  assignee?: RequestCardAssignee;
  /** Requester name (who created the request) */
  requesterName?: string;
  /** Memo/description text */
  memo?: string;
  users?: User[];
  /** Transaction/request amount */
  amount?: number;
  /** Request status (Open, In Progress, Closed) - legacy, use workState instead */
  status?: 'Open' | 'In Progress' | 'Closed';
  /** Work state - lifecycle stage of the request */
  workState?: WorkState;
  /** Due date for the request */
  dueDate?: Date;
  /** Whether this request has a mention of the current user */
  hasMention?: boolean;
  /** Whether this request has attachments */
  hasAttachment?: boolean;
  /** Whether this is an internal request (not visible to client) */
  isInternal?: boolean;
  /** Whether this card is the focused/active item (for keyboard nav) */
  isFocused?: boolean;
  /** Whether this card is in the current selection set */
  isSelected?: boolean;
  /** Whether multi-select mode is active (shows checkboxes) */
  isMultiSelectMode?: boolean;
  /** Search query for highlighting matching text */
  searchQuery?: string;
  /** Click handler with event for modifier key detection */
  onClick?: (e: React.MouseEvent) => void;
  /** Checkbox change handler */
  onCheckChange?: (checked: boolean) => void;
};

/**
 * RequestCard - Minimal card component for displaying a request in the browser list.
 * Currently shows: title, date, avatar.
 * Will be extended later with: status icon, tags, due date, progress, etc.
 */
/** Work state icon configuration */
const workStateConfig: Record<WorkState, { icon: React.ReactNode; label: string; colorVar: string }> = {
  backlog: { icon: <IconWSBacklog />, label: 'Backlog', colorVar: '--ks-light-grey' },
  todo: { icon: <IconWSBacklog />, label: 'Not Started', colorVar: '--ks-light-grey' },
  'in-progress': { icon: <IconWSInProgress />, label: 'In Progress', colorVar: '--ks-chart-dark-orangish-solid' },
  blocked: { icon: <IconPromote />, label: 'Blocker', colorVar: '--ks-blocker' },
  closed: { icon: <IconWSClosed />, label: 'Completed', colorVar: '--ks-chart-dark-green-bluish' },
};

export const RequestCard = ({
  id,
  title,
  date,
  dateTooltip,
  assignee,
  requesterName: _requesterName,
  memo,
  users,
  amount,
  status: _status,
  workState,
  dueDate,
  hasMention = false,
  hasAttachment = false,
  isInternal = false,
  isFocused = false,
  isSelected = false,
  isMultiSelectMode = false,
  searchQuery,
  onClick,
  onCheckChange,
}: RequestCardProps) => {
  const prefix = prefixClass('ksq2-request-card');

  const memoPreview = memo ? formatMessageContentToPlainText(memo, { users }) : undefined;
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCheckChange?.(!isSelected);
  };
  const effectiveDueDate = dueDate instanceof Date && !Number.isNaN(dueDate.getTime()) ? dueDate : null;

  // Format amount as currency
  const formatAmount = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent);
    }
  };

  return (
    <div
      className={classNames(prefix(), {
        [prefix('--focused')]: isFocused,
        [prefix('--selected')]: isSelected,
        [prefix('--multi-select')]: isMultiSelectMode,
        [prefix(`--${workState}`)]: !!workState,
      })}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-request-id={id}
      role="option"
      aria-selected={isSelected}
      tabIndex={isFocused ? 0 : -1}
    >
      <div className={prefix('__leftColumn')}>
        <AvatarMini
          id={id}
          type={assignee?.type ?? 'unassigned'}
          name={assignee?.name}
          variant={assignee ? (assignee.isExplicit ? 'assigned' : 'fallback') : undefined}
          tooltip={true}
        />
        <div className={prefix('__checkbox-area')}>
          <input
            type="checkbox"
            className={prefix('__checkbox')}
            checked={isSelected}
            onChange={() => onCheckChange?.(!isSelected)}
            onClick={handleCheckboxClick}
            tabIndex={-1}
          />
        </div>
      </div>

      <div className={prefix('__content')}>
        <div className={prefix('__header')}>
          {dateTooltip ? (
            <Tooltip content={dateTooltip} showDelay={800}>
              <div className={prefix('__date')}>{date}</div>
            </Tooltip>
          ) : (
            <div className={prefix('__date')}>{date}</div>
          )}
          <div className={prefix('__indicator-container')}>
            <div className={prefix('__flags')}>
              {hasAttachment && (
                <Tooltip content="Has attachments" showDelay={800}>
                  <div className={prefix('__flags--atachment')}>
                    <IconAttachment />
                  </div>
                </Tooltip>
              )}
              {hasMention && (
                <Tooltip content="You were mentioned" showDelay={800}>
                  <div className={prefix('__flags--mention')}>
                    <IconMention />
                  </div>
                </Tooltip>
              )}
              {isInternal && (
                <div className={prefix('__flags--internal')}>
                  <Tags tags={[{ tag: 'int', color: 'green', tagType: 'pill', toolTip: 'Internal Thread' }]} />
                </div>
              )}
            </div>
            {workState && (
              <Tooltip content={workStateConfig[workState].label} showDelay={800}>
                <span
                  className={classNames(prefix('__work-state'), prefix(`__work-state--${workState}`))}
                  style={{ color: `var(${workStateConfig[workState].colorVar})` }}
                >
                  {workStateConfig[workState].icon}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className={prefix('__title')}>
          <HighlightText text={title} query={searchQuery} />
        </div>
        {(memoPreview || amount !== undefined) && (
          <div className={prefix('__memo-row')}>
            {memoPreview && (
              <div className={prefix('__memo')}>
                <HighlightText text={memoPreview} query={searchQuery} />
              </div>
            )}
            {amount !== undefined && <div className={prefix('__amount')}>{formatAmount(amount)}</div>}
          </div>
        )}
        {/* <div className={prefix('__meta')}>
          {requesterName && <span className={prefix('__requester')}>{requesterName}</span>}
        </div> */}
        {effectiveDueDate && (
          <div className={prefix('__due-date')}>
            <DueDateBadge date={effectiveDueDate} status={effectiveDueDate < new Date() ? 'past-due' : 'pending'} />
          </div>
        )}
      </div>
    </div>
  );
};
